import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { StudyLocalizationStatus } from '@axon/rosetta-sdk';
import { FirebaseAppContext } from 'libs.firebase_react';
import { useLocalizeMessage, useToastNotification, Spinner } from 'libs.nucleus';
import { useBooleanState, ButtonWithTooltip } from 'libs.react';
import { StudyTranslationStatusModal } from './study_translation_status_modal.component';
import { TranslationWorkbenchContext } from '../../contexts';
import { useSetStudyStatus } from '../../hooks';
import { LockToggleOffIcon } from '../icons/lock-toggle-off.icon';
import { LockToggleOnIcon } from '../icons/lock-toggle-on.icon';
const toStudyLocalizationStatus = (status) => String(status).toLowerCase() === 'ready' ? StudyLocalizationStatus.READY : StudyLocalizationStatus.DRAFT;
export const StudyTranslationStatusWorkflow = ({ study, updateStudy, isUpgradeAvailable, }) => {
    const t = useLocalizeMessage();
    const { studyId } = useParams();
    const { addNotification } = useToastNotification();
    const [isModalOpen, openModal, closeModal] = useBooleanState(false);
    // True from job submit until the status-change job completes or fails.
    const [isApplying, setIsApplying] = useState(false);
    const { localizationStatus: studyLocalizationStatus = StudyLocalizationStatus.NOT_STARTED } = study || {};
    const { resourcesAssets, studyAssets, notificationsAssets, locales, libraryAssets, isReadyForTranslation, isStudyEnglishOnly, } = useContext(TranslationWorkbenchContext);
    const { logEvent } = useContext(FirebaseAppContext);
    // The status-change job resolves asynchronously; read the latest study/updateStudy
    // through refs so the completion handler never closes over a stale first-render copy.
    const studyRef = useRef(study);
    const updateStudyRef = useRef(updateStudy);
    // Status the study had before the in-flight change, so a failed job can be reverted
    // after the optimistic flip below.
    const previousStatusRef = useRef(studyLocalizationStatus);
    useEffect(() => {
        studyRef.current = study;
        updateStudyRef.current = updateStudy;
    }, [study, updateStudy]);
    const applyLocalizationStatus = useCallback((status, l10nMapResourceId) => {
        const currentStudy = studyRef.current;
        // Emit a NEW study reference. `assignStudy` calls `setCurrentStudy`, which bails on
        // a same-reference update — so mutating in place makes a disable (which only flips
        // localizationStatus, leaving l10nMapResourceId unchanged) invisible in the UI.
        const nextStudy = Object.assign(Object.create(Object.getPrototypeOf(currentStudy)), currentStudy);
        nextStudy.localizationStatus = status;
        if (l10nMapResourceId) {
            nextStudy.l10nMapResourceId = l10nMapResourceId;
        }
        updateStudyRef.current(nextStudy);
    }, []);
    const { mutate: setStudyStatus } = useSetStudyStatus({
        onStatusChanged: ({ localizationStatus, l10nMapResourceId }) => {
            // Reconcile with the authoritative backend result (also captures the published map id).
            applyLocalizationStatus(toStudyLocalizationStatus(localizationStatus), l10nMapResourceId);
            setIsApplying(false);
        },
        onStatusError: () => {
            // Revert the optimistic flip so the UI matches the unchanged backend state.
            applyLocalizationStatus(previousStatusRef.current);
            setIsApplying(false);
            addNotification({
                title: t('Failed to update translation status. Please try again.'),
                type: 'error',
            });
        },
    });
    const assets = { ...resourcesAssets, ...studyAssets, ...notificationsAssets };
    const assetData = Object.values(assets)
        .filter((asset) => Boolean(asset.asset))
        .map((asset) => ({
        ...asset.asset,
        preview: {
            ...(asset.asset.preview || {}),
            default_product_version: study.productTemplate,
        },
    }));
    const allAssets = { ...resourcesAssets, ...studyAssets, ...notificationsAssets, ...libraryAssets };
    const allIdString = Object.keys(allAssets).join(',');
    const isNotReady = studyLocalizationStatus !== StudyLocalizationStatus.READY;
    const hasLocales = !isStudyEnglishOnly && locales.length > 1;
    const buildAssetLocaleVersionSet = useCallback(() => {
        const assetLocaleVersionSet = [];
        locales.forEach((locale) => {
            Object.values(allAssets).forEach((asset) => {
                if (!asset.translationWorkbenchId ||
                    !asset?.currentVersionStatus?.[locale] ||
                    Number(asset?.latestVersionStatus?.[locale]?.version || 0) === 0) {
                    return;
                }
                assetLocaleVersionSet.push({
                    id: asset.translationWorkbenchId,
                    locale,
                    version: Number(asset?.currentVersionStatus?.[locale].version),
                });
            });
        });
        return assetLocaleVersionSet;
    }, [allIdString, locales]);
    const handleStatusChange = useCallback(async () => {
        // The enable/disable orchestration runs as a backend job; close the modal and show
        // progress on the status button until the job completes.
        if ((studyLocalizationStatus === StudyLocalizationStatus.DRAFT ||
            studyLocalizationStatus === StudyLocalizationStatus.NOT_STARTED) &&
            assets) {
            previousStatusRef.current = studyLocalizationStatus;
            logEvent('translations_enable', { studyId });
            setIsApplying(true);
            closeModal();
            setStudyStatus({
                studyId: Number(studyId),
                status: 'ready',
                assets: assetData,
                assetLocaleVersionSet: buildAssetLocaleVersionSet(),
            });
        }
        if (studyLocalizationStatus === StudyLocalizationStatus.READY) {
            previousStatusRef.current = studyLocalizationStatus;
            logEvent('translations_disable', { studyId });
            setIsApplying(true);
            closeModal();
            // Optimistically flip to DRAFT before the job resolves. The provider's
            // `useL10nMapSync` only runs while the study is READY; if we leave it READY during
            // the ~5s disable job it republishes the study l10n map and PUTs the config back to
            // `ready`, clobbering the backend's draft write. Flipping now stops that race
            // (reverted in `onStatusError` if the job fails).
            applyLocalizationStatus(StudyLocalizationStatus.DRAFT);
            setStudyStatus({ studyId: Number(studyId), status: 'draft' });
        }
        return false;
    }, [
        applyLocalizationStatus,
        assetData,
        assets,
        buildAssetLocaleVersionSet,
        logEvent,
        setStudyStatus,
        studyLocalizationStatus,
        studyId,
    ]);
    const toggleMessage = t('Enable translations');
    const disabledTooltip = !hasLocales
        ? isStudyEnglishOnly
            ? t('Your study is configured as en-US only, translation services are disabled.')
            : t('Add locales to the study configuration to use translation services.')
        : '';
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: 'flex gap-4 items-center', children: _jsx(ButtonWithTooltip, { tooltip: isApplying ? t('Applying the translation status change. This may take a moment…') : disabledTooltip, enableTooltip: isApplying || isStudyEnglishOnly || !hasLocales, tooltipPlacement: 'bottom', label: t('Change status'), onClick: openModal, variant: 'secondary', content: isApplying ? (_jsxs(_Fragment, { children: [_jsx(Spinner, { size: 'sm' }), _jsx("span", { className: 'ml-2 nucleus-text-sm', children: toggleMessage })] })) : isNotReady ? (_jsxs(_Fragment, { children: [_jsx(LockToggleOffIcon, {}), _jsx("span", { className: 'ml-2 nucleus-text-sm', children: toggleMessage })] })) : (_jsxs(_Fragment, { children: [_jsx(LockToggleOnIcon, { disabled: !isReadyForTranslation || isUpgradeAvailable }), _jsx("span", { className: 'ml-2 nucleus-text-sm', children: toggleMessage })] })), disabled: isApplying || !isReadyForTranslation || (!isNotReady && isUpgradeAvailable) || !hasLocales }) }), isModalOpen && (_jsx(StudyTranslationStatusModal, { onModalClose: closeModal, onHandleStatusChange: handleStatusChange, isLoading: isApplying, currentStatus: studyLocalizationStatus, isUpgradeAvailable: isUpgradeAvailable }))] }));
};
